import { useCallback, useEffect, useState } from "react";
import { useRestClientContext } from "../contexts/RestClientContext";
import { Accessor, ColumnSelectionPreset, Constituent, PropertyAttribute } from "../types/types";
import Case from "case";
import { useTeamContext } from "../contexts/TeamContext";

type AccessorsByConstituent = Record<Constituent, Accessor[]>; // NB: I suppose we could further narrow this down with unions on only the relevant accessors
type ColumnMetadata = {
	friendly_name?: string;
	is_visible?: boolean;
	datatype: string;
	default_column_width?: number;
};
export type TypesByAccessor = Record<Accessor | PropertyAttribute, ColumnMetadata>;
type AccessorsWithMetadata = Record<Constituent, TypesByAccessor>;

export interface UsePropertyAccessors {
	loading: boolean;
	accessors: Accessor[];
	constituents: Constituent[];
	accessorsByConstituent: AccessorsByConstituent;
	typesByAccessor: TypesByAccessor;
	propertyAttributes: PropertyAttribute[];
	columnSelectionPresets: ColumnSelectionPreset[];
	getFriendlyColumnName: (accessor: Accessor | PropertyAttribute) => string;
	getColumnWidth: (accessor: Accessor | PropertyAttribute) => number | undefined;
}

export default function usePropertyAccessors(): UsePropertyAccessors {
	const client = useRestClientContext();
	const team = useTeamContext();
	const [loading, setLoading] = useState(true);

	const [accessors, setAccessors] = useState<Accessor[]>([]);
	const [accessorsMeta, setAccessorsMeta] = useState<AccessorsWithMetadata>();
	const [constituents, setConstituents] = useState<Constituent[]>([]);
	const [accessorsByConstituent, setAccessorsByConstituent] = useState<AccessorsByConstituent>(
		{} as AccessorsByConstituent
	);
	const [propertyAttributes, setPropertyAttributes] = useState<PropertyAttribute[]>([]);
	const [typesByAccessor, setTypesByAccessor] = useState<Record<Accessor | PropertyAttribute, ColumnMetadata>>(
		{} as TypesByAccessor
	);
	const [columnSelectionPresets, setColumnSelectionPresets] = useState<ColumnSelectionPreset[]>([]);

	useEffect(() => {
		client
			.fetchPropertyColumns(team.id)
			.then((response) => response.json())
			.then((json) => {
				setColumnSelectionPresets(json.presets ?? []);

				const constituents = Object.keys(json.constituents) as Constituent[];
				const accessors = Object.entries(json.constituents as Record<Constituent, Record<string, string>>)
					.map(([name, fields]) => Object.keys(fields).map((field) => `${name}.${field}` as Accessor))
					.flat();

				const accessorsByConstituent = Object.fromEntries(
					constituents.map((name) => [
						name,
						Object.keys(json.constituents[name]).map(
							(attribute: string) => `${name}.${attribute}`
						) as Accessor[],
					])
				) as Record<Constituent, Accessor[]>;

				const typesByAccessor = {
					...Object.entries(json.constituents).reduce(
						(acc, [k, v]) => ({
							...acc,
							...(typeof v === "object" && !Array.isArray(v)
								? Object.fromEntries(Object.entries(v!).map(([k2, v2]) => [`${k}.${k2}`, v2]))
								: { [k]: v }),
						}),
						{}
					),
					...json.property,
				} as TypesByAccessor;

				setConstituents(constituents);
				setAccessors(accessors);
				setAccessorsByConstituent(accessorsByConstituent);
				setTypesByAccessor(typesByAccessor);
				setAccessorsMeta(json.constituents);
				setPropertyAttributes(Object.keys(json.property) as PropertyAttribute[]);

				setLoading(false);
			});
	}, [client, team.id]);

	const getFriendlyColumnName = useCallback(
		(column: Accessor | PropertyAttribute): string => {
			if (!column.includes(".")) {
				if (typesByAccessor[column]?.friendly_name) {
					return typesByAccessor[column].friendly_name!;
				}
				// Format the property attributes on the front end. This currently cannot be changed in the
				// friendly_names table, but this may be requested in the future.
				return Case.title(column).replace("Uprn", "UPRN").replace("Rdsap", "RDSAP").replace("Rrn", "RRN");
			}

			const accessorSplit = column.split(".");
			const constituent = accessorSplit[0] as Constituent;
			const accessorColumn = accessorSplit[1] as Accessor;

			if (
				accessorsMeta &&
				accessorsMeta[constituent] &&
				accessorsMeta[constituent][accessorColumn]?.friendly_name
			) {
				return accessorsMeta[constituent][accessorColumn]?.friendly_name ?? Case.title(accessorColumn);
			}

			return Case.title(accessorColumn);
		},
		[accessorsMeta, typesByAccessor]
	);

	const getColumnWidth = useCallback(
		(column: Accessor | PropertyAttribute): number | undefined => {
			return typesByAccessor[column]?.default_column_width ?? undefined;
		},
		[typesByAccessor]
	);

	return {
		loading,
		accessors,
		constituents,
		accessorsByConstituent,
		typesByAccessor,
		propertyAttributes,
		columnSelectionPresets,
		getFriendlyColumnName,
		getColumnWidth,
	};
}
